// @ts-check
import { gql } from '@apollo/client/core';
import { Font } from '@react-pdf/renderer';
import React from 'react';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import { PagesProvider } from '../../components/Page';
import MyListCoverPage from './MyList.CoverPage';
import MyListIndexPage from './MyList.Index';
import MyListPage from './MyList.Page';
import messages from './locale/messages';

// Disable word hyphenation
Font.registerHyphenationCallback((word) => [word]);

const productsPerPage = 6;
export default function MyList({
  data,
  locale,
  user,
  ...rest
}: {
  data: MyListPageProps;
  locale: string;
  user: any;
}) {
  const { pages, index } = afterQuery(data);

  // create an array of items with 2 pages each
  const dualPages = Array.from(Array(Math.ceil(pages.length / 2))).map(
    (_, index) => {
      return pages.slice(index * 2, index * 2 + 2);
    }
  );

  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        <MyListCoverPage collection={data.collection} user={user} {...rest} />
        <MyListIndexPage items={index} collection={data.collection} {...rest} />
        {dualPages.map((page, index) => (
          <MyListPage
            key={`bookletpage-${index}`}
            items={page}
            collection={data.collection}
            {...rest}
          />
        ))}
      </UserLocaleProvider>
    </PagesProvider>
  );
}

export type MyListPageProps = {
  siteData: {
    exhibitors: {
      all: Exhibitor[];
      findById: (id: string) => Exhibitor | undefined;
    };
    products: {
      all: Product[];
      findById: (id: string) => Product | undefined;
    };
    tastingNotes: {
      all: TastingNote[];
      findById: (id: string) => TastingNote | undefined;
    };
    bookmarks: {
      byExhibitorId: Record<string, BookmarkedExhibitor>;
      exhibitors: BookmarkedExhibitor[];
    };
  };
  collection: Collection;
};

function afterQuery(data: MyListPageProps) {
  const {
    siteData: { exhibitors, products, tastingNotes, bookmarks },
  } = data;

  const exhibitorProductSummary = products.all.reduce((acc, product) => {
    if (!acc[product.exhibitorId])
      acc[product.exhibitorId] = { denominations: [], primaryVarieties: [] };
    const primaryVariety = product.grapeVarieties?.[0]?.varietyName;
    acc[product.exhibitorId].denominations.push(product.denomination);
    if (primaryVariety)
      acc[product.exhibitorId].primaryVarieties.push(primaryVariety);
    return acc;
  }, {} as Record<string, { denominations: string[]; primaryVarieties: string[] }>);

  // Fetch data for bookmarks
  const resolved = bookmarks?.exhibitors
    ?.map((bookmarkedExhibitor) => {
      const exhibitor = exhibitors.findById(bookmarkedExhibitor.exhibitorId);
      // Let's fail gently if the exhibitor is not found
      // This can happen if a bookmarked exhibitor is removed from an event
      if (!exhibitor) return null;
      const resolvedProducts = bookmarkedExhibitor.products.map(
        (bookmarkedProduct) => {
          const product = products.findById(bookmarkedProduct.id);
          const tastingNote = tastingNotes.findById(bookmarkedProduct.id);
          return {
            ...product,
            ...bookmarkedProduct,
            tastingNote,
          };
        }
      );
      return {
        ...exhibitor,
        ...exhibitorProductSummary[exhibitor.exhibitorId],
        ...bookmarkedExhibitor.bookmark,
        products: resolvedProducts,
      };
    })
    .filter(nonNullable);

  // Hack so that typescript recognizes that the array is non-nullable
  function nonNullable<T>(value: T): value is NonNullable<T> {
    return value !== null && value !== undefined;
  }

  // Sort by exhibitor stand name or exhibitor name
  resolved.sort((a, b) => {
    const aName = a.stand?.name || a.name;
    const bName = b.stand?.name || b.name;
    return aName.localeCompare(bName);
  });

  type ResolvedPage = typeof resolved[number];

  const pages = resolved.reduce((acc, item, index) => {
    const products = item.products;
    if (!products.length) {
      acc.push(item);
      return acc;
    }
    const pagedProducts = Array.from(
      Array(Math.ceil(products.length / productsPerPage))
    ).map((_, index) => {
      return products.slice(
        index * productsPerPage,
        index * productsPerPage + productsPerPage
      );
    });
    pagedProducts.forEach((products, index) => {
      acc.push({ ...item, products });
    });
    return acc;
  }, [] as ResolvedPage[]);

  return {
    index: resolved,
    pages,
  };
}

export type MyListPageItem = ReturnType<typeof afterQuery>['pages'][0];

MyList.fragment = gql`
  fragment MyList on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      name
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
      logo {
        # 55mm in 300dpi
        fixed(width: 654, crop: PAD, gravity: NORTH, fetch_format: JPG) {
          src
        }
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...MyList_Registration
    }
  }

  fragment MyList_Registration on RegistrationCatalogSection {
    registration {
      registrationId
      name
      companyId
      stand {
        name
      }
      profile {
        ...MyList_Profile
      }

      registeredProducts {
        nodes {
          ...MyList_RegisteredProduct
        }
      }
    }
  }

  fragment MyList_Profile on Profile {
    name
    street
    postalCode
    city
    country
    telephone
    email
    website
    shortDescription
    autoSizedLogo: logo {
      # 55mm in 300dpi
      fixed(width: 654, crop: PAD, gravity: NORTH, fetch_format: JPG) {
        src
      }
    }
    autoSizedMainImage: mainImage {
      # 55mm in 300dpi
      fixed(
        width: 1105
        height: 891
        crop: FILL
        gravity: FACE_AUTO
        fetch_format: JPG
      ) {
        src
      }
    }
  }

  fragment MyList_RegisteredProduct on RegisteredProduct {
    producer {
      isSeekingDistribution
      distributionContacts {
        contactId
        companyName
      }
    }
    productId
    product {
      productId
      name
      alcohol

      regionHierarchy
      denomination
      bottleImage {
        # This doesn't matter because the URL is recalculated by Image component
        # This should replaced with sourceUrl when it returns a String
        fixed(height: 1, width: 1, crop: PAD) {
          src
        }
      }
      ... on Wine {
        designation
        residualSugar
        totalAcidity
        grapeVarieties {
          percentage
          varietyName
        }
      }
    }
  }
`;

type Bookmark = {
  id: string;
  addedAt: Date;
};

type ExhibitorBookmark = Bookmark & {
  type: 'registration';
};

type ProductBookmark = Bookmark & {
  type: 'product';
};

type BookmarkedExhibitor = {
  exhibitorId: string;
  bookmark?: ExhibitorBookmark;
  products: ProductBookmark[];
};

type TastingNote = {
  productId: string;
  note: string;
  rating: number;
  addedAt: Date;
  updatedAt: Date;
};

type Product = {
  productId: string;
  shortName: string;
  exhibitorId: string;
  exhibitor: Exhibitor;
  countryName: string;
  countryCode: string;
  denomination: string;
  region: string;
  subregion: string;
  producer: {
    name: string;
  };
  grapeVarieties: {
    percentage: number;
    varietyName: string;
  }[];
  bottleImage: {
    publicId: string;
  };
  classification: string;
  designation: string;
};

type Exhibitor = {
  exhibitorId: string;
  name: string;
  city: string;
  countryName: string;
  countryCode: string;
  stateProvince: string;
  logo: {
    publicId: string;
    fixed: {
      src: string;
    };
  };
  stand?: {
    name: string;
  };
  brandId: string;
};

export type Collection = {
  eventId: string;
  name: string;
  site: {
    publicUrl: string;
  };
  logo: {
    publicId: string;
    fixed: {
      src: string;
    };
  };
};
